/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-get, ember/no-mixins, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { isArray } from '@ember/array';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import GroupEditMixin from 'linced-app/mixins/group-edit';
import { CalculatedParams } from '@linc-technologies/linced-common/enums/group-auto-groups';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import Options from '@linc-technologies/linced-common/enums/options';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import moment from 'moment';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class GroupCard extends Component.extend(GroupEditMixin) {
  Scopes = Scopes;

  // Services
  @service
  features;

  @service
  options;

  @service
  permissions;

  @service
  roles;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  toaster;

  @tracked
  isConfirmDuplicateOpen = false;

  // Attrs
  @optionAlias(Options.PROGRESS_REPORTING_ENABLED)
  progressionsEnabled;

  @alias('features.sidekickEnabled')
  sidekickEnabled;

  @alias('features.linkedGoalsEnabled')
  linkedGoalsEnabled;

  // Props
  // See: GroupEditMixin for inherited props.
  // group provides the group to render a card for.
  group = null;

  // onClose provides a function to be called when the group card is closed.
  onClose() {}

  // State
  activeTab = 0;

  // autoGroup provides a cleaner boolean switch for templates rather than
  // having to do multiple enum comparisons in the template.
  @computed('group.type')
  get autoGroup() {
    let group = this.group;
    return GroupType.AUTO === group.type;
  }

  @computed('session.{personId,scopes.[]}', 'group.{type,currentStaff.[]}')
  get canEdit() {
    if (this.permissions.meet('urn:linced:organisation:role:admin')) {
      // Tenant Admins and above can edit any Group.
      return true;
    }
    if (
      this.group.type === GroupType.GROUP ||
      this.group.type === GroupType.SHARED
    ) {
      // Tenant Staff can edit My and Shared Groups if they're currently members.
      let isStaff = this.permissions.meet('urn:linced:organisation:role:staff');
      let isInGroup = !!findBy(
        this.group.currentStaff,
        'personId',
        this.session.personId,
      );
      if (isStaff && isInGroup) {
        return true;
      }
    }

    // Not allowed to edit.
    return false;
  }

  // assessmentPages holds all pages marked as "hasAssessments", used to
  // dynamically generate links to whole group assessment data. `store.query`
  // resolves to a PromiseArray and the template `{{#each}}`es this, so resolve
  // it into a tracked property first — ember-data deprecates array access on
  // PromiseArray.
  @tracked
  assessmentPages = [];

  didInsertElement() {
    super.didInsertElement(...arguments);
    this.loadAssessmentPages();
  }

  async loadAssessmentPages() {
    let pages = await this.store.query('posts/page', {
      schoolId: this.get('session.activeSchool'),
      type: PersonType.LEARNER,
      hasAssessments: true,
      visibleInMenu: true,
    });
    if (this.isDestroyed || this.isDestroying) {
      return;
    }
    this.assessmentPages = pages;
  }

  @computed('person.type')
  get pages() {
    let type = this.get('person.type');
    if (type) {
      return this.store
        .peekAll('posts/page')
        .filter((page) => page.type === type);
    } else {
      return [];
    }
  }

  @computed('session.scopes.[]')
  get isAdmin() {
    return this.permissions.meet(Scopes.TENANT_ADMIN);
  }

  @tracked currentYear = moment().year();

  @computed('currentYear', 'group.year')
  get isNonCurrentYear() {
    const year = this.get('group.year');
    // A year of 0 (the Go zero value) means no year.
    return !!year && this.currentYear !== year;
  }

  @computed('isNonCurrentYear')
  get cardClasses() {
    let classNames = ['group-card', 'flex-xs-90', 'flex-sm-80', 'flex-70'];
    if (this.isNonCurrentYear) {
      classNames.push('non-current-year');
    }
    return classNames.join(' ');
  }

  @computed('group.{type,params}')
  get includeFormers() {
    if (this.group.get('type') !== GroupType.AUTO) {
      return false;
    }
    return this.group.get('params.type') === CalculatedParams.RECENT_LEAVERS;
  }

  @optionAlias('core:staffPermissions:medicationsForTeachers')
  medicationsForTeachers;

  @computed('isAdmin', 'medicationsForTeachers')
  get canAdministerMedications() {
    return this.isAdmin || this.medicationsForTeachers;
  }

  @action
  close() {
    this.onClose(false);
  }

  @action
  editGroup() {
    super.editGroup(this.group.id);
  }

  @action
  async duplicateGroup() {
    // ConfirmationDialog is controlled - we own `isConfirmDuplicateOpen` and
    // have to clear it ourselves. `closeImpl` also unmounts this card today,
    // but the flag must not be left stuck open if that ever changes.
    this.isConfirmDuplicateOpen = false;
    // A belongsTo takes a record, not the async proxy.
    let school = await this.group.schoolId;
    if (this.isDestroying || this.isDestroyed) {
      return;
    }
    this.closeImpl();
    let groupJson = this.group.serialize();
    delete groupJson.id;
    delete groupJson.externalId;
    delete groupJson.heroId;
    let thisYear = moment().year();
    if (groupJson.year !== thisYear) {
      // Duplicating into a new year - set the new group's year to the current calendar year; leave name/label alone.
      groupJson.year = thisYear;
    } else {
      // Duplicating into the same year - prepend "Copy of" to name/label for differentiation.
      groupJson.name = `Copy of ${groupJson.name}`;
      if (groupJson.label) {
        groupJson.label = `Copy of ${groupJson.label}`;
      }
    }
    groupJson.schoolId = school;
    let newRecord = this.store.createRecord('group', groupJson);
    newRecord.save().then((newGroup) => {
      this.router.transitionTo('group', newGroup).then(() => {
        this.toaster.success(
          `Group "${newGroup.get('name')}" created successfully!`,
        );
      });
    });
  }

  @action
  attendance() {
    this.closeImpl();
    this.router.transitionTo('group.attendance', this.group);
  }

  @action
  goToRoute(route, identifiers, queryParams) {
    let router = this.router;

    this.closeImpl();

    let options = {
      queryParams,
    };

    if (isArray(identifiers)) {
      router.transitionTo(route, ...identifiers, options);
    } else {
      router.transitionTo(route, identifiers, options);
    }
  }

  @action
  placeholder() {}

  closeImpl() {
    this.onClose();
    if (!isEmpty(this.extraOnClose)) {
      this.extraOnClose(true);
    }
  }
}
